/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type InviteClinicUserInput } from '@therapy/shared-validators';

import { apiClient } from './client.js';
import { type ParentInvite } from './resources.js';

export type { InviteClinicUserInput };
/** Invite result reuses the parent-invite shape (email + activation link). */
export type ClinicUserInvite = ParentInvite;

export type ClinicUserStatus = 'invited' | 'active' | 'disabled';
export type ClinicUserRole = 'CLINIC_ADMIN' | 'THERAPIST' | 'FINANCE';

export interface ClinicUser {
  readonly id: string;
  readonly email: string;
  readonly fullName: string;
  readonly status: ClinicUserStatus;
  readonly roles: readonly ClinicUserRole[];
  readonly lastLoginAt: string | null;
  readonly createdAt: string;
}

const BASE = '/clinic-users';

export const clinicUsersApi = {
  async list(): Promise<readonly ClinicUser[]> {
    const { data } = await apiClient.get<readonly ClinicUser[]>(BASE);
    return data;
  },
  async invite(input: InviteClinicUserInput): Promise<ClinicUserInvite> {
    const { data } = await apiClient.post<ClinicUserInvite>(BASE, input);
    return data;
  },
  async setStatus(id: string, status: 'active' | 'disabled'): Promise<readonly ClinicUser[]> {
    const { data } = await apiClient.patch<readonly ClinicUser[]>(`${BASE}/${id}/status`, {
      status,
    });
    return data;
  },
};
